import { useState, type FormEvent } from 'react';
import { api, EchecApi } from '../partage/api/client';
import { AccueilSansCompte, ChampEmail, ChampMotDePasse } from './AccueilSansCompte';
import './Connexion.css';

/** La règle du serveur (`server/src/domain/comptes/inscription.ts`), redite ici pour ne pas gâcher un essai. */
const MOT_DE_PASSE_LONGUEUR_MIN = 8;

/**
 * La création d'un compte avec le code d'invitation (chantier comptes, ticket 03). Pas de
 * planche : faite dans l'app, dans le style de la connexion, comme la spec du chantier le dit.
 * Le compte créé, la session ne s'ouvre qu'avec le lien du mail (chantier mails) : `onAVerifier`
 * mène à « Regarde ta boîte mail ». Une adresse qui a déjà un compte y mène aussi : l'écran ne
 * dit rien de plus que le serveur.
 */
export function Inscription({ onAVerifier, onRetour }: { onAVerifier: (email: string) => void; onRetour: () => void }) {
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [code, setCode] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const pret = email.trim() !== '' && motDePasse !== '' && code.trim() !== '';

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    if (!pret || enCours) return;

    if ([...motDePasse].length < MOT_DE_PASSE_LONGUEUR_MIN) {
      setRefus(MOT_DE_PASSE_TROP_COURT);
      return;
    }

    setEnCours(true);
    setRefus(null);
    try {
      await api.inscription(email, motDePasse, code);
      onAVerifier(email);
    } catch (erreur) {
      setRefus(messageDeRefus(erreur));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <AccueilSansCompte titre="Créer mon carnet" description="Avec le code que tu as reçu.">
      <form className="connexion-carte" onSubmit={soumettre}>
        <label className="connexion-etiquette" htmlFor="code">
          Code d&apos;invitation
        </label>
        <div className="connexion-ligne">
          <input
            id="code"
            className="connexion-saisie connexion-saisie--texte"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            autoFocus
            spellCheck={false}
            maxLength={200}
          />
        </div>

        <label className="connexion-etiquette" htmlFor="email">
          E-mail
        </label>
        <ChampEmail id="email" valeur={email} onChange={setEmail} sansFocus />

        <label className="connexion-etiquette" htmlFor="mot-de-passe">
          Mot de passe
        </label>
        <ChampMotDePasse
          id="mot-de-passe"
          valeur={motDePasse}
          onChange={setMotDePasse}
          autoComplete="new-password"
        />
        <p className="connexion-aide">Au moins {MOT_DE_PASSE_LONGUEUR_MIN} caractères.</p>

        <button className="connexion-entrer" type="submit" disabled={!pret || enCours}>
          {enCours ? 'Un instant' : 'Créer mon compte'}
        </button>

        {refus && (
          <p className="refus connexion-refus" role="status">
            {refus}
          </p>
        )}
      </form>

      <button type="button" className="connexion-lien" onClick={onRetour}>
        J&apos;ai déjà un compte
      </button>
    </AccueilSansCompte>
  );
}

const MOT_DE_PASSE_TROP_COURT = `Le mot de passe doit faire au moins ${MOT_DE_PASSE_LONGUEUR_MIN} caractères.`;

/**
 * Aucun refus n'est rouge. Une adresse déjà prise n'est pas un refus : elle mène à « Regarde ta
 * boîte mail », comme une inscription réussie (chantier mails).
 */
function messageDeRefus(erreur: unknown): string {
  if (!(erreur instanceof EchecApi)) return 'Quelque chose n’a pas répondu. Réessaie.';
  switch (erreur.code) {
    case 'inscription_refusee':
      return 'Inscription refusée : vérifie le code.';
    case 'inscription_fermee':
      return 'L’inscription est fermée pour l’instant.';
    case 'email_invalide':
      return 'Cette adresse n’a pas la forme d’un e-mail.';
    case 'mot_de_passe_trop_court':
      return MOT_DE_PASSE_TROP_COURT;
    case 'hors_ligne':
      return 'Pas de réseau. L’inscription a besoin d’en avoir.';
    default:
      return erreur.statut === 429
        ? 'Trop de tentatives. Laisse passer quelques minutes.'
        : 'Quelque chose n’a pas répondu. Réessaie.';
  }
}
